import FormatUtils from "./FormatUtils.js";

/** Supported per-column format identifiers for configured CSV export. */
const COLUMN_FORMATS = Object.freeze({
  date: "date",
  dateTime: "dateTime",
});

/**
 * Formats a raw cell value for CSV output.
 * @param {unknown} value
 * @param {string} [format] - Optional {@link COLUMN_FORMATS} key
 * @returns {string}
 */
function formatCellValue(value, format) {
  if (value === null || value === undefined) {
    return "";
  }

  if (format === COLUMN_FORMATS.date) {
    return FormatUtils.formatIsoDate(value);
  }

  if (format === COLUMN_FORMATS.dateTime) {
    return FormatUtils.formatDateTime(value);
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

/** @param {string} cell */
function escapeCsvCell(cell) {
  if (/[",\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
}

/**
 * Reads a nested value from an object using dot notation.
 * @param {Record<string, unknown>} obj
 * @param {string} path
 * @returns {unknown}
 */
export function getValueByPath(obj, path) {
  if (!path) {
    return undefined;
  }

  let current = obj;
  for (const part of path.split(".")) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== "object") {
      return undefined;
    }
    current = /** @type {Record<string, unknown>} */ (current)[part];
  }

  return current;
}

/**
 * @param {unknown} value
 * @param {string} [prefix]
 * @param {Record<string, string>} out
 */
function flattenValue(value, prefix, out) {
  if (value === null || value === undefined) {
    if (prefix) {
      out[prefix] = "";
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      flattenValue(item, prefix ? `${prefix}.${index}` : String(index), out);
    });
    return;
  }
  if (typeof value === "object" && !(value instanceof Date)) {
    Object.entries(value).forEach(([key, nested]) => {
      flattenValue(nested, prefix ? `${prefix}.${key}` : key, out);
    });
    return;
  }
  if (prefix) {
    out[prefix] = formatCellValue(value);
  }
}

/** @param {Record<string, unknown>} row */
function flattenRow(row) {
  const flat = {};
  flattenValue(row, "", flat);
  delete flat[""];
  return flat;
}

/** @param {Array<Record<string, unknown>>} rows */
function buildFlattenAllCsv(rows) {
  const flatRows = rows.map(flattenRow);
  const headers = [...new Set(flatRows.flatMap((r) => Object.keys(r)))].sort();

  const lines = [
    headers.map(escapeCsvCell).join(","),
    ...flatRows.map((row) => headers.map((h) => escapeCsvCell(row[h] ?? "")).join(",")),
  ];
  return lines.join("\n");
}

/**
 * @param {import("../config/CsvExportConfigs.js").CsvExportColumnConfig} column
 * @param {Map<string, string>} headerLookup
 * @returns {string}
 */
function resolveColumnHeader(column, headerLookup) {
  if (column.header) {
    return column.header;
  }

  const fromGrid = headerLookup.get(column.field);
  if (fromGrid) {
    return fromGrid;
  }

  return column.field;
}

/**
 * @param {import("@mui/x-data-grid").GridColDef[]} gridColumns
 * @returns {Map<string, string>}
 */
function buildHeaderLookup(gridColumns) {
  const lookup = new Map();

  for (const column of gridColumns) {
    if (column.type === "actions" || !column.field || !column.headerName) {
      continue;
    }
    lookup.set(column.field, column.headerName);
  }

  return lookup;
}

/**
 * @param {Array<Record<string, unknown>>} rows
 * @param {import("../config/CsvExportConfigs.js").CsvExportColumnConfig[]} columns
 * @param {import("@mui/x-data-grid").GridColDef[]} gridColumns
 * @returns {string}
 */
function buildConfiguredCsv(rows, columns, gridColumns) {
  const headerLookup = buildHeaderLookup(gridColumns);
  const headers = columns.map((column) => resolveColumnHeader(column, headerLookup));

  const lines = [
    headers.map(escapeCsvCell).join(","),
    ...rows.map((row) =>
      columns
        .map((column) => {
          const path = column.path ?? column.field;
          return escapeCsvCell(formatCellValue(getValueByPath(row, path), column.format));
        })
        .join(","),
    ),
  ];

  return lines.join("\n");
}

/**
 * Builds a CSV string from row objects (full export).
 * @param {Array<Record<string, unknown>>} rows
 * @param {{ exportConfig?: import("../config/CsvExportConfigs.js").CsvExportConfig, gridColumns?: import("@mui/x-data-grid").GridColDef[] }} [options]
 * @returns {string}
 */
export function buildFullCsv(rows, options = {}) {
  if (!rows.length) {
    return "";
  }

  const { exportConfig, gridColumns = [] } = options;

  if (exportConfig?.exportAll || !exportConfig?.columns?.length) {
    return buildFlattenAllCsv(rows);
  }

  return buildConfiguredCsv(rows, exportConfig.columns, gridColumns);
}
